<template>
  <div class="flex items-center flex-col w-full">
    <div class="flex w-full">
      <div class="basis-[80%] min-w-0">
        <InputRub
          v-model="model"
          class="w-full"
          :invalid="invalid"
          :readonly="readonly"
          :disabled="disabled"
          :placeholder="placeholder"
          :inputId="inputId"
          :pt="{
            input: {
              root: 'border-r-0 rounded-r-none'
            }
          }"
          @blur="emits('blur', $event)"
          @focus="emits('focus', $event)"
        />
      </div>

      <div class="basis-[20%] min-w-0">
        <InputNumber
          v-model="percent"
          :invalid="invalid"
          :readonly="readonly"
          :disabled="disabled"
          :max="Number(maxPercent) || 50"
          :min="minPercent"
          :max-fraction-digits="2"
          suffix=" %"
          class="w-full"
          :pt="{
            root: 'w-full',
            input: {
              root: 'w-full max-h-8 text-right border-l-0 rounded-s-none rounded-e-xl border-[#BDBDBD]'
            }
          }"
          @blur="emits('blur', $event)"
          @focus="emits('focus', $event)"
        />
      </div>
    </div>

    <div
      v-if="showSlider"
      class="mt-3 w-full"
    >
      <Slider
        v-model="uiPercent"
        :min="Number(minPercent)"
        :max="Number(maxPercent) || 50"
        :step="1"
        :disabled="disabled"
        class="w-full"
        :class="{ 'opacity-50': disabled }"
        :pt="{
          handle: 'bg-[#262626] rounded-full w-3 h-3 top-1.5 border border-[#262626] ',
          range: 'bg-[#262626]'
        }"
      />
      <div class="flex justify-between text-xs text-gray-500 mt-1">
        <span>{{ minPercent }}%</span>
        <span>{{ maxPercent || 50 }}%</span>
      </div>

      <SelectButton
        v-model="percent"
        :options="presetOptions"
        optionLabel="label"
        optionValue="value"
        :allowEmpty="false"
        :disabled="disabled"
        class="flex flex-wrap gap-1 text-xs mt-2"
        :pt="{
          button: ({ context }) => [
            'text-[13px] p-2 rounded-xl border transition border-solid',
            context.active
              ? 'bg-[#3F3F3F] text-white border-[#BDBDBD]'
              : 'bg-white text-[#3F3F3F] border-gray-300 hover:bg-gray-50'
          ]
        }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { watchIgnorable } from '@vueuse/core'
import { computed, defineModel, ref, watch } from 'vue'
import { InputRub } from '@/shared'
import InputNumber, { InputNumberBlurEvent } from 'primevue/inputnumber'
import Slider from 'primevue/slider'
import SelectButton from 'primevue/selectbutton'

const props = withDefaults(
  defineProps<{
    amount?: number | null
    showSlider?: boolean
    minPercent?: number
    maxPercent?: number
    minAmount?: number
    invalid?: boolean
    sliderStep?: number
    readonly?: boolean
    disabled?: boolean
    placeholder?: string
    inputId?: string
  }>(),
  {
    minPercent: 0.1
  }
)

const emits = defineEmits<{
  blur: [InputNumberBlurEvent]
  focus: [Event]
}>()

const model = defineModel<number | null>()
const percent = ref<number>(props.minPercent ?? 0)

const uiPercent = computed<number>({
  get: () => {
    const p = Number(percent.value ?? props.minPercent ?? 0)
    const min = props.minPercent ?? 0
    return Math.min(Math.max(p, min), 100)
  },
  set: (v: number) => {
    const min = props.minPercent ?? 0
    const next = Math.min(Math.max(v, min), 100)
    percent.value = next
  }
})

if (model.value != null && props.amount != null) {
  setPercent()
}

const { ignoreUpdates: ignoreUpdatesModelAmount } = watchIgnorable([model, () => props.amount], () => {
  ignoreUpdatesPercent(() => {
    setPercent()
  })
})

const { ignoreUpdates: ignoreUpdatesPercent } = watchIgnorable(percent, percent => {
  ignoreUpdatesModelAmount(() => {
    if (props.amount != null) {
      // Добавляем 0.1%, но не выше 100%
      const adjustedPercent = percent === 0.1 ? percent : Math.min((percent ?? 0) + 0.1, 100)
      model.value = Math.floor((props.amount / 100) * adjustedPercent)
    } else {
      model.value = null
    }
  })
})

type PercentOption = { label: string; value: number }

const presetOptions = computed<PercentOption[]>(() => {
  const min = props.minPercent ?? 0
  const max = Number(props.maxPercent) || 50
  const allowed = [20, 30, 50]

  return allowed.filter(v => v >= min && v <= max).map(v => ({ label: `${v}%`, value: v }))
})

watch(
  percent,
  val => {
    const min = Number(props.minPercent ?? 0)
    if (val < min) {
      percent.value = min
    }
  },
  { immediate: true }
)

watch(
  () => props.minPercent,
  (min = 0) => {
    if (percent.value < min) percent.value = min
  }
)

watch(
  () => props.maxPercent,
  max => {
    const m = Number(max) || 50
    if (percent.value > m) percent.value = m
  }
)
function setPercent() {
  if (model.value != null && props.amount != null) {
    percent.value = Math.round((model.value / (props.amount / 100)) * 10) / 10
  } else {
    // не null, а минимум
    percent.value = props.minPercent ?? 0
  }
}
</script>
